// src/hooks/usePosts.js
import { useState, useEffect, useContext } from 'react';
import { UserContext } from '../context/userContext';
import postService from '../services/postService';

/**
 * Hook for post operations and state
 */
const usePosts = (initialPosts = []) => {
  const [posts, setPosts] = useState(initialPosts);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });
  
  const { currentUser } = useContext(UserContext);

  // Fetch posts with pagination
  const fetchPosts = async (page = 1, limit = 10) => {
    setLoading(true);
    try {
      const response = await postService.getPosts(page, limit);
      setPosts(response.posts);
      setPagination({
        page,
        limit,
        total: response.pagination.total,
        pages: response.pagination.pages
      });
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to fetch posts');
      console.error('Error fetching posts:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch user feed
  const fetchFeed = async (page = 1, limit = 10) => {
    if (!currentUser) return;
    
    setLoading(true);
    try {
      const response = await postService.getFeed(page, limit);
      setPosts(response.posts);
      setPagination({
        page,
        limit,
        total: response.pagination.total,
        pages: response.pagination.pages
      });
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to fetch feed');
      console.error('Error fetching feed:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch posts by category
  const fetchPostsByCategory = async (category, page = 1, limit = 10) => {
    setLoading(true);
    try {
      const response = await postService.getPostsByCategory(category, page, limit);
      setPosts(response.posts);
      setPagination({
        page,
        limit,
        total: response.pagination.total,
        pages: response.pagination.pages
      });
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to fetch category posts');
      console.error('Error fetching category posts:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch posts by user
  const fetchUserPosts = async (userId, page = 1, limit = 10) => {
    setLoading(true);
    try {
      const response = await postService.getUserPosts(userId, page, limit);
      setPosts(response.posts);
      setPagination({
        page,
        limit,
        total: response.pagination.total,
        pages: response.pagination.pages
      });
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to fetch user posts');
      console.error('Error fetching user posts:', err);
    } finally {
      setLoading(false);
    }
  };

  // Search posts
  const searchPosts = async (query, page = 1, limit = 10) => {
    if (!query) return;
    
    setLoading(true);
    try {
      const response = await postService.searchPosts(query, page, limit);
      setPosts(response.posts);
      setPagination({
        page,
        limit,
        total: response.pagination.total,
        pages: response.pagination.pages
      });
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to search posts');
      console.error('Error searching posts:', err);
    } finally {
      setLoading(false);
    }
  };

  // Get trending posts
  const fetchTrendingPosts = async (page = 1, limit = 10) => {
    setLoading(true);
    try {
      const response = await postService.getTrendingPosts(page, limit);
      setPosts(response.posts);
      setPagination({
        page,
        limit,
        total: response.pagination.total,
        pages: response.pagination.pages
      });
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to fetch trending posts');
      console.error('Error fetching trending posts:', err);
    } finally {
      setLoading(false);
    }
  };

  // Create a post
  const createPost = async (postData, onProgress) => {
    if (!currentUser) return null;
    
    setLoading(true);
    try {
      const newPost = await postService.createPost(postData, onProgress);
      setPosts(prev => [newPost, ...prev]);
      setError(null);
      return newPost;
    } catch (err) {
      setError(err.message || 'Failed to create post');
      console.error('Error creating post:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Update a post
  const updatePost = async (postId, postData, onProgress) => {
    if (!currentUser) return null;
    
    setLoading(true);
    try {
      const updatedPost = await postService.updatePost(postId, postData, onProgress);
      setPosts(prev => 
        prev.map(post => post._id === postId ? updatedPost : post)
      );
      setError(null);
      return updatedPost;
    } catch (err) {
      setError(err.message || 'Failed to update post');
      console.error('Error updating post:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Delete a post
  const deletePost = async (postId) => {
    if (!currentUser) return false;
    
    setLoading(true);
    try {
      await postService.deletePost(postId);
      setPosts(prev => prev.filter(post => post._id !== postId));
      setError(null);
      return true;
    } catch (err) {
      setError(err.message || 'Failed to delete post');
      console.error('Error deleting post:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Toggle like on a post
  const toggleLike = async (postId) => {
    if (!currentUser) return null;
    
    try {
      const response = await postService.toggleLike(postId);
      
      // Update post in state
      setPosts(prev => 
        prev.map(post => {
          if (post._id === postId) {
            return {
              ...post,
              liked: response.liked,
              likes: response.liked ? post.likes + 1 : post.likes - 1,
              reaction: response.reaction
            };
          }
          return post;
        })
      );
      
      return response;
    } catch (err) {
      console.error('Error toggling like:', err);
      return null;
    }
  };

  // Share a post
  const sharePost = async (postId, comment = '') => {
    if (!currentUser) return null;
    
    try {
      const sharedPost = await postService.sharePost(postId, comment);
      setPosts(prev => [sharedPost, ...prev]);
      return sharedPost;
    } catch (err) {
      console.error('Error sharing post:', err);
      return null;
    }
  };

  return {
    posts,
    loading,
    error,
    pagination,
    fetchPosts,
    fetchFeed,
    fetchPostsByCategory,
    fetchUserPosts,
    searchPosts,
    fetchTrendingPosts,
    createPost,
    updatePost,
    deletePost,
    toggleLike,
    sharePost
  };
};

export default usePosts;